import React from "react"
import { useFormikContext } from "formik"
import MyEditorField from "@common-utils/fields/MyEditorField"
import MySelectField from "@common-utils/fields/MySelectField"
import MyDateDayMonthYearField from "@common-utils/fields/MyDateDayMonthYearField"
import MySelectSearchDataFromServer from "@common-utils/fields/MySelectSearchDataFromServer"
import MySelectGetAllDataFromServer from "@common-utils/fields/MySelectGetAllDataFromServer"
import MyFileField from "@common-utils/fields/MyFileField"
import {
     CONST_CANDIDAT_REMUNERATION_SOUHAITE_BRUT_NET_OPTIONS,
     CONST_CANDIDAT_REMUNERATION_SOUHAITE_JOUR_OR_MOIS_OR_ANNEE_OPTIONS,
     CONST_DISPONIBILITE_DATE_EXACTE,
     CONST_DISPONIBILITE_OPTIONS_FOR_SELECT2,
     CONST_MOBILITE_DEPARTEMENTAL,
     CONST_MOBILITE_OPTIONS_FOR_SELECT2,
     CONST_MOBILITE_PAR_VILLE,
     CONST_MOBILITE_REGIONAL,
} from "@common-constants/*"
import { IFormValuesModel } from "./LogicEntretienTel"
import MyLoadingOverlay from "@common-components/MyLoadingOverlay"
import MyCheckboxFormField from "@common-components/fields/MyCheckboxFormField"
import MyInputFormField from "@common-components/fields/MyInputFormField"
import { CONST_API_UTILS_GET_DEPARTEMENTS_URL_ENDPOINT, CONST_API_UTILS_GET_REGIONS_URL_ENDPOINT, CONST_API_UTILS_GET_VILLES_URL_ENDPOINT } from "../../../../../../endpoints"
import clsx from "clsx"
import MySelectFormField from "@common-components/fields/MySelectFormField"

interface IProps {
     isFetchingContent: boolean
     forceEditorToRefreshContent?: { value: string; random: string }
}

/* Entretien DMS (téléphonique) */
const EntretienTelFormFields: React.FC<IProps> = ({ isFetchingContent, forceEditorToRefreshContent }) => {
     // We need the full formik context to pass to MyInputFormField
     const formikProps = useFormikContext<IFormValuesModel>()
     const { values, setFieldValue, errors } = formikProps

     const feelings = [
          { id: "FEELING_LIKE", icon: "fa-thumbs-up", color: "success", label: "Positif" },
          { id: "FEELING_NEUTRAL", icon: "fa-meh", color: "warning", label: "Neutre" },
          { id: "FEELING_DISLIKE", icon: "fa-thumbs-down", color: "danger", label: "Négatif" },
     ]

     return (
          <>
               {/* Notes Section */}
               <div className="mb-5">
                    <label className="col-form-label fw-bold fs-6">Notes</label>
                    <MyLoadingOverlay isActive={isFetchingContent}>
                         <MyEditorField
                              value={values.notes}
                              height={250}
                              onChange={val => setFieldValue("notes", val)}
                              placeholder={"Saisissez vos notes d'entretien..."}
                              forceEditorToRefreshContent={forceEditorToRefreshContent}
                         />
                    </MyLoadingOverlay>
                    {errors.notes && <div className="text-danger mt-1">{errors.notes}</div>}
               </div>

               {/* Feeling Section */}
               <div className="mb-10 bg-light-white p-5 rounded border border-dashed border-gray-300">
                    <label className="fw-boldest fs-6 text-gray-700 mb-4 d-block text-uppercase">Ressenti de l'entretien</label>
                    <div className="d-flex align-items-center gap-4 mb-4">
                         {feelings.map(f => (
                              <button
                                   key={f.id}
                                   type="button"
                                   className={clsx("btn btn-icon btn-xl btn-circle transition-all", values.feeling === f.id ? `btn-${f.color} shadow-sm scale-110` : "btn-light")}
                                   onClick={() => setFieldValue("feeling", values.feeling === f.id ? "" : f.id)}
                              >
                                   <i className={clsx("fas fs-2", f.icon, values.feeling === f.id ? "text-white" : `text-${f.color}`)} />
                              </button>
                         ))}
                         {values.feeling && (
                              <span className="text-gray-600 fw-bold fs-7 ms-2 animate__animated animate__fadeIn">{feelings.find(f => f.id === values.feeling)?.label}</span>
                         )}
                    </div>

                    {values.feeling && (
                         <div className="animate__animated animate__fadeIn">
                              <textarea
                                   className="form-control form-control-solid fs-7"
                                   rows={2}
                                   placeholder="Un petit commentaire sur ce ressenti ?"
                                   value={values.feelingCommentaire}
                                   onChange={e => setFieldValue("feelingCommentaire", e.target.value)}
                              />
                         </div>
                    )}
               </div>

               {/* Disponibilité */}
               <div className="row mb-5">
                    <label className="col-lg-4 col-form-label fw-bold fs-6">Disponibilité</label>
                    <div className="col-lg-8">
                         <MySelectField
                              name={"disponibilite"}
                              options={CONST_DISPONIBILITE_OPTIONS_FOR_SELECT2}
                              value={values.disponibilite}
                              onChange={val => setFieldValue("disponibilite", val)}
                              isInvalid={!!errors.disponibilite}
                              isSearchable={false}
                         />
                         {errors.disponibilite && <div className="text-danger mb-2">{errors.disponibilite}</div>}

                         {values.disponibilite === CONST_DISPONIBILITE_DATE_EXACTE && (
                              <div className="mt-2">
                                   <MyDateDayMonthYearField
                                        name={"disponibiliteDate"}
                                        value={values.disponibiliteDate}
                                        placeholder={"Date exacte"}
                                        onChange={val => setFieldValue("disponibiliteDate", val)}
                                        isInvalid={!!errors.disponibiliteDate}
                                   />
                                   {errors.disponibiliteDate && <div className="text-danger">{errors.disponibiliteDate}</div>}
                              </div>
                         )}
                    </div>
               </div>

               {/* Mobilité */}
               <div className="row mb-5">
                    <label className="col-lg-4 col-form-label fw-bold fs-6">Mobilité</label>
                    <div className="col-lg-8">
                         <MySelectField
                              name={"mobilite"}
                              options={CONST_MOBILITE_OPTIONS_FOR_SELECT2}
                              value={values.mobilite}
                              isInvalid={!!errors.mobilite}
                              onChange={val => setFieldValue("mobilite", val)}
                              isSearchable={false}
                         />
                         {errors.mobilite && <div className="text-danger mb-2">{errors.mobilite}</div>}

                         {values.mobilite === CONST_MOBILITE_DEPARTEMENTAL && (
                              <div className="mt-3">
                                   <MySelectSearchDataFromServer
                                        name={"mobiliteDepartements"}
                                        value={values.mobiliteDepartements}
                                        onChange={val => setFieldValue("mobiliteDepartements", val)}
                                        url={CONST_API_UTILS_GET_DEPARTEMENTS_URL_ENDPOINT}
                                        isInvalid={!!errors.mobiliteDepartements}
                                        method={"GET"}
                                        minimumLengthSearch={3}
                                        isMulti
                                        placeholder="Rechercher des départements..."
                                   />
                              </div>
                         )}
                         {values.mobilite === CONST_MOBILITE_REGIONAL && (
                              <div className="mt-3">
                                   <MySelectGetAllDataFromServer
                                        name={"mobiliteRegions"}
                                        value={values.mobiliteRegions}
                                        onChange={val => setFieldValue("mobiliteRegions", val)}
                                        isInvalid={!!errors.mobiliteRegions}
                                        url={CONST_API_UTILS_GET_REGIONS_URL_ENDPOINT}
                                        isMulti
                                        placeholder="Sélectionner des régions..."
                                   />
                              </div>
                         )}
                         {values.mobilite === CONST_MOBILITE_PAR_VILLE && (
                              <div className="mt-3">
                                   <MySelectSearchDataFromServer
                                        name={"mobiliteVilles"}
                                        value={values.mobiliteVilles}
                                        onChange={val => setFieldValue("mobiliteVilles", val)}
                                        url={CONST_API_UTILS_GET_VILLES_URL_ENDPOINT}
                                        isInvalid={!!errors.mobiliteVilles}
                                        method={"GET"}
                                        minimumLengthSearch={3}
                                        isMulti
                                        placeholder="Rechercher des villes..."
                                   />
                              </div>
                         )}
                    </div>
               </div>

               {/* Rémunération */}
               <MySelectFormField
                    label="Rémunération souhaitée"
                    name="remunerationSouhaiteFormat"
                    formikProps={formikProps}
                    options={CONST_CANDIDAT_REMUNERATION_SOUHAITE_BRUT_NET_OPTIONS}
                    placeholder="Base (Brut / Net / HT)"
                    classNameLayout="mb-2"
                    classNameLabel="col-lg-4"
                    classNameField="col-lg-8"
               />

               <MySelectFormField
                    name="remunerationSouhaiteJourOrMensuelOrAnnuel"
                    formikProps={formikProps}
                    placeholder="Fréquence (Par jour / mois / année)"
                    options={CONST_CANDIDAT_REMUNERATION_SOUHAITE_JOUR_OR_MOIS_OR_ANNEE_OPTIONS}
                    classNameLayout="mb-2"
                    classNameLabel="col-lg-4"
                    classNameField="col-lg-8"
               />

               <MyInputFormField
                    name="remunerationSouhaiteMontant"
                    formikProps={formikProps}
                    placeholder="ex: 35000"
                    inputMode="numeric"
                    inputRightSideIcon="euro-sign"
                    classNameLayout="mb-5"
                    classNameLabel="col-lg-4"
                    classNameField="col-lg-8"
               />

               {/* Document upload Checkbox */}
               <MyCheckboxFormField
                    label="Pièce jointe"
                    name="voulezVousJoindreUnDocumentToThisSuivi"
                    text="Joindre un document à ce suivi"
                    formikProps={formikProps}
                    classNameLayout={values.voulezVousJoindreUnDocumentToThisSuivi ? "mb-2" : "mb-5"}
                    classNameLabel="col-lg-4"
                    classNameField="col-lg-8"
               />

               {/* File Input (Shown only if checkbox is checked) */}
               {values.voulezVousJoindreUnDocumentToThisSuivi && (
                    <div className="row mb-5">
                         <div className="col-lg-8 offset-lg-4">
                              <div className="p-3 bg-light border border-dashed rounded">
                                   <MyFileField name={"document"} isInvalid={!!errors.document} onChange={val => setFieldValue("document", val)} />
                                   {errors.document && <div className="text-danger mt-1">{errors.document}</div>}
                              </div>
                         </div>
                    </div>
               )}
          </>
     )
}

export default EntretienTelFormFields
